import * as React from "react";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../models/states/StoreState";
import {
    setCarrouselActiveVariableIndex,
    validationClear,
    validationSetResult,
} from "../../redux/actions";
import useStyles from "./CarrouselControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import {
    useValidation,
    useDiscreteVariadicValidator,
    isAnswerRequired,
} from "../../hooks/useValidation";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceListControl } from "./ChoiceListControl";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { QuestionType } from "../../models/enums/QuestionType";
import { isQuestionSoftPrompt } from "../../models/utilities/questionUtilities";
import lottieGif from "../../assets/images/lottie.gif";
import { MOBILE_WIDTH, SMALL_WIDTH } from "../../consts";
import useRecall from "../../hooks/useRecall";

interface StatementProps {
    id: string;
    key: string;
    text: string;
    color: string;
    imageUrl: string;
    isMobileView: boolean;
    isActiveItem: boolean;
    statementWidth: number;
    handleSwipe: (direction: string) => void;
    isShowLottieGesture: boolean;
}

const MOBILE_DIMENSIONS = 270;
const RESIZE_CAROUSEL = "resize-carousel";
const MOBILE_VIEW_GAP = 16;
const DESKTOP_VIEW_GAP = 64;
const DESKTOP_STATEMENT_WIDTH = 376;
const OFFSET_MARGIN_FOR_MOBILE = 20;
const PARTIAL_WIDTH_FOR_MOBILE = 20; // for prev and next statement

const Statement = (props: StatementProps) => {
    const {
        id,
        color,
        text,
        imageUrl,
        isMobileView,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
    } = props;
    const recalledText = useRecall(text);
    const recalledImage = useRecall(imageUrl);
    const theme: any = useTheme();
    const { ContentResponsivenessOption: contentResponsivenessOption } = theme;
    const justifyContent = recalledImage ? "space-between" : "center";
    const imageWrapperRef = React.useRef(null);
    const classes = useStyles({
        theme,
        color,
        justifyContent,
        isMobileView,
        contentResponsivenessOption,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
        imageUrl: recalledImage,
    });
    const containerRef = React.useRef(null);
    const imageRef = React.useRef(null);
    const textContainerRef = React.useRef(null);
    const [forceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        const container = containerRef.current;
        const image = imageRef.current;
        const wrapperContainer = imageWrapperRef.current;
        if (image) {
            image.addEventListener("load", responsiveRender);
        } else {
            resizeFont();
        }

        return () => {
            image && image.removeEventListener("load", responsiveRender);
        };

        function responsiveRender() {
            resizeCarousel(image, container, wrapperContainer);
            resizeFont();
        }

        function resizeFont() {
            const fontSize = getFontSize(container);
            container.style.fontSize = `${fontSize}px`;
        }
    }, [recalledImage, isMobileView, contentResponsivenessOption]);
    const isStatementHasContent = recalledText.replace("<br>", "").trim().length > 0;
    const resizeCarousel = (image, container, wrapperContainer) => {
        if (!image) return;
        const originalWidth = image.naturalWidth;
        const isSmallImage = statementWidth > originalWidth;
        const imageWidth = statementWidth < originalWidth ? "100%" : `${originalWidth}px`;
        image.style.width = imageWidth;
        container.style.width = `${statementWidth}px`;
        if (isSmallImage) {
            wrapperContainer.style.paddingTop = "16px";
        }
        if (isSmallImage && !isStatementHasContent) {
            wrapperContainer.style.paddingBottom = "16px";
        }
    };

    const getFontSize = (container) => {
        const DEFAULT_FONT_SIZE = 16;
        const MINIMUM_FONT_SIZE = 12;
        const MAXIMUM_FONT_SIZE = 30;
        const containerWidth = container.offsetWidth;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        context.font = `${DEFAULT_FONT_SIZE}px sans-serif`;
        const textWidth = context.measureText(recalledText).width;

        let fontSize = Math.floor((containerWidth / textWidth) * DEFAULT_FONT_SIZE);
        fontSize = Math.max(fontSize, MINIMUM_FONT_SIZE);
        fontSize = Math.min(fontSize, MAXIMUM_FONT_SIZE);

        if (textWidth < containerWidth && fontSize < DEFAULT_FONT_SIZE) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        if (contentResponsivenessOption === RESIZE_CAROUSEL) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        return fontSize;
    };

    const ClampedHTML = ({ htmlContent, elementId }) => {
        return (
            <span
                className={isActiveItem ? "active-statement" : ""}
                id={elementId}
                style={{
                    display: "block",
                    WebkitLineClamp: "unset",
                    WebkitBoxOrient: "unset",
                    overflow: "hidden",
                    textOverflow: "clip",
                    wordWrap: "break-word",
                    lineHeight: "1.2em",
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        );
    };

    const [touchStartX, setTouchStartX] = React.useState(null);
    const [touchEndX, setTouchEndX] = React.useState(null);
    const SWIPE_THRESHOLD = 20; // Minimum distance for a valid swipe

    React.useEffect(() => {
        const handleTouchStart = (e) => {
            setTouchStartX(e.touches[0].clientX);
            setTouchEndX(null); // Reset touch end
        };

        const handleTouchMove = (e) => {
            setTouchEndX(e.touches[0].clientX);
        };

        const handleTouchEnd = () => {
            if (touchStartX === null || touchEndX === null) return;

            const deltaX = touchStartX - touchEndX;
            if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
                if (deltaX > 0) {
                    props.handleSwipe("next"); // Swipe Left detected
                } else {
                    props.handleSwipe("prev"); // Swipe Right detected
                }
            }
            // Reset swipe values
            setTouchStartX(null);
            setTouchEndX(null);
        };

        const element = containerRef.current;
        if (element) {
            element.addEventListener("touchstart", handleTouchStart);
            element.addEventListener("touchmove", handleTouchMove);
            element.addEventListener("touchend", handleTouchEnd);
        }

        return () => {
            if (element) {
                element.removeEventListener("touchstart", handleTouchStart);
                element.removeEventListener("touchmove", handleTouchMove);
                element.removeEventListener("touchend", handleTouchEnd);
            }
        };
    }, [touchStartX, touchEndX]);

    return (
        <div className={classes.statement} id={id} ref={containerRef}>
            {recalledImage && (
                <span className="image-wrapper" ref={imageWrapperRef}>
                    <img
                        className={`image-element`}
                        src={recalledImage}
                        ref={imageRef}
                        alt={recalledImage}
                    />
                </span>
            )}
            {isStatementHasContent && (
                <div className="statementTextContent" aria-hidden="false" ref={textContainerRef}>
                    <ClampedHTML
                        elementId="statementTextContent"
                        htmlContent={recalledText.trim()}
                        key={forceUpdateKey}
                    />
                </div>
            )}
        </div>
    );
};

const WrapperWithNavigationButtons = ({
    classes,
    isPrevBtnActive,
    isNextBtnActive,
    handleNavigationButtonClick,
    backButtonText,
    nextButtonText,
    isNumberNavigation,
    carrouselActiveVariableIndex,
    sequencedVariables,
    mentions,
}) => {
    const prevButtonRef = React.useRef(null);
    const nextButtonRef = React.useRef(null);
    const isKeyboardNavigation = React.useRef(false);

    React.useEffect(() => {
        if (!isPrevBtnActive && document.activeElement === prevButtonRef.current) {
            nextButtonRef.current.focus();
        } else if (!isNextBtnActive && document.activeElement === nextButtonRef.current) {
            prevButtonRef.current.focus();
        }
        isKeyboardNavigation.current = false;
    }, [isPrevBtnActive, isNextBtnActive]);

    return (
        <div className={classes.navigationButtonsPanel} role="radiogroup">
            <div
                className="fixedWidth"
                role="radio"
                ref={prevButtonRef}
                aria-checked={isPrevBtnActive}
                tabIndex={isPrevBtnActive ? 0 : -1}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("prev");
                    }
                }}
                onClick={() => handleNavigationButtonClick("prev")}
                aria-label={backButtonText}
            >
                {isPrevBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-left"]}
                        fixedWidth
                        className={classes.navigationButton}
                    />
                )}
            </div>
            {(() => {
                if (isNumberNavigation) {
                    return (
                        <CounterNavigation
                            carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                            totalLength={sequencedVariables.length}
                        />
                    );
                } else {
                    return (
                        <BulletNavigation
                            mentions={mentions}
                            carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                            sequencedVariables={sequencedVariables}
                            classes={classes}
                        />
                    );
                }
            })()}
            <div
                className="fixedWidth"
                role="radio"
                ref={nextButtonRef}
                aria-checked={!isPrevBtnActive && isNextBtnActive}
                tabIndex={isNextBtnActive ? 0 : -1}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("next");
                    }
                }}
                onClick={() => handleNavigationButtonClick("next")}
                aria-label={nextButtonText}
            >
                {isNextBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-right"]}
                        fixedWidth
                        className={classes.navigationButton}
                    />
                )}
            </div>
        </div>
    );
};

const BulletNavigation = ({
    mentions,
    sequencedVariables,
    carrouselActiveVariableIndex,
    classes,
}) => {
    const itemsToDisplay = 6;
    const showFirstEllipse =
        itemsToDisplay < sequencedVariables.length &&
        itemsToDisplay / 2 < carrouselActiveVariableIndex + 1;
    const showLastEllipse =
        itemsToDisplay < sequencedVariables.length &&
        carrouselActiveVariableIndex < sequencedVariables.length - 4;
    const fixedTransformValue = 25;
    const [transformValue, setTransformValue] = React.useState(0);
    React.useEffect(() => {
        let newTransformValue = transformValue;
        if (sequencedVariables.length > 6) {
            if (
                carrouselActiveVariableIndex > 2 &&
                sequencedVariables.length - carrouselActiveVariableIndex > 3
            ) {
                newTransformValue = -fixedTransformValue * (carrouselActiveVariableIndex - 2);
            } else if (carrouselActiveVariableIndex < 3) {
                newTransformValue = 0;
            } else if (newTransformValue == 0 && carrouselActiveVariableIndex > itemsToDisplay) {
                newTransformValue =
                    -fixedTransformValue * (sequencedVariables.length - itemsToDisplay);
            }
            if (newTransformValue !== transformValue) {
                setTransformValue(newTransformValue); // Update state only if there's a change
            }
        }
    });
    return (
        <div className={classes.bulletNavigationPanel} role="radiogroup">
            <div className="ellipse-item">{showFirstEllipse && <div></div>}</div>
            <div className="bullets-container">
                {sequencedVariables.map((variable, index) => {
                    const activeItem = carrouselActiveVariableIndex === index;
                    const answeredItem =
                        mentions.hasOwnProperty(variable.name) && mentions[variable.name].length;
                    const className =
                        activeItem && answeredItem
                            ? "active-answered-item"
                            : activeItem && !answeredItem
                            ? "active-not-answered-item"
                            : "";
                    return (
                        <div
                            key={index}
                            style={{
                                transform: `translateX(${transformValue}px)`,
                                transition: "transform 0.8s ease-in-out",
                            }}
                            className={className}
                        >
                            {activeItem && answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : answeredItem && !activeItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : activeItem && !answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        fill="#316AEE"
                                        stroke="white"
                                        strokeWidth="2"
                                    />
                                </svg>
                            ) : (
                                <div className="bullet" role="radio"></div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="ellipse-item">{showLastEllipse && <div></div>}</div>
        </div>
    );
};
const CounterNavigation = ({ carrouselActiveVariableIndex, totalLength }) => {
    return (
        <div
            className="counter-navigation"
            aria-label={`${carrouselActiveVariableIndex + 1}/${totalLength}`}
        >{`${carrouselActiveVariableIndex + 1}/${totalLength}`}</div>
    );
};

export function CarrouselControl(props: ControlProps) {
    const {
        question,
        sequencedVariables,
        validateSignal,
        mentions,
        questionnaire,
        isDesktopPreviewView,
        showSoftPrompt,
    } = props;
    const questionHasAtleastOneVariable = sequencedVariables && sequencedVariables.length > 0;

    // This condition only occurs if all the statements is eliminated then only question description is displayed
    if (!questionHasAtleastOneVariable) {
        return "";
    }

    const instanceId = `${question.id}_statement`;
    const isNumberNavigation = question.settings.NavigationStyle != "Bullets";
    let hideVariableBackArrow = false;
    let canLoopVariable = false;
    let advanceAutomatically = false;
    let choiceListAnswerType = QuestionType[QuestionType.CheckBox];
    let activeStatementMentionsCount = 0;
    const questionName = question.name;
    const dispatch = useDispatch();

    if (hasSetting(question, "HideVariableBackArrow")) {
        hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
    }

    if (hasSetting(question, "LoopVariable")) {
        canLoopVariable = getSettingBoolean(question, "LoopVariable");
    }

    if (hasSetting(question, "AutoAdvanceVariable")) {
        advanceAutomatically = getSettingBoolean(question, "AutoAdvanceVariable");
    }

    if (hasSetting(question, "CarrouselChoiceListAnswerType")) {
        choiceListAnswerType = getSettingString(question, "CarrouselChoiceListAnswerType");
    }

    const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
    const [language] = useLanguage();
    const theme = useTheme();
    const counter = React.useRef(1);
    const containerRef = React.useRef<HTMLDivElement>();
    const [deviceWidth] = React.useState<number>(window.innerWidth);
    const [deviceHeight] = React.useState<number>(window.innerHeight);
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const isMobileView = !isDesktopPreviewView && (deviceWidth <= MOBILE_WIDTH || mobilePreview);
    const backButtonAriaLabelText = translate(questionnaire.theme, "BackButton", language);
    const nextButtonAriaLabelText = translate(questionnaire.theme, "NextButton", language);
    const isAboveChoiceNavigation = question.settings?.NavigationDisplay?.includes("Above") ?? true;
    const isBelowChoiceNavigation =
        question.settings?.NavigationDisplay?.includes("Below") ?? false;
    const justifyContent = isMobileView ? "space-between" : "center";
    const classes = useStyles({ theme, isMobileView, justifyContent, isAboveChoiceNavigation });
    const [statementValidation, setStatementValidationCheck] = React.useState({
        action: "NONE",
        direction: "NONE",
    });
    const [directionSaved, setDirection] = React.useState("NONE");
    const isPrevBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isPrevBtnActive || false,
    );
    const isNextBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isNextBtnActive || false,
    );
    const carrouselActiveVariableIndex = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.activeVariableIndex || 0,
    );
    const errorMonitor = useSelector(
        (state: StoreState) => state.interview.validation.errorsByVariableName,
    );
    let prevErrorMonitorRef = React.useRef(null);
    let activeVariable = sequencedVariables[carrouselActiveVariableIndex];
    const [isCarouselVisible, setIsCarouselVisible] = React.useState(false);
    const [isFirstTimeRender, setIsFirstTimeRender] = React.useState(true);
    const [showLottieGesture, setShowLottieGesture] = React.useState(true);
    const isShowLottieGesture = showLottieGesture && isMobileView;
    React.useEffect(() => {
        const questionContainerHeight = containerRef.current?.offsetHeight;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isMobileView) {
                    setIsCarouselVisible(entry.isIntersecting); // Update visibility state
                }
            },
            {
                root: null, // Use the viewport as the root
                threshold: questionContainerHeight > deviceHeight ? 0.5 : 0.9, // Trigger based on viewport
            },
        );
        if (containerRef.current) {
            observer.observe(containerRef.current); // Start observing the carousel container
        }
        // Cleanup the observer on component unmount
        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        if (isCarouselVisible) {
            if (isFirstTimeRender) {
                setIsFirstTimeRender(false);
                if (showLottieGesture) {
                    setTimeout(() => {
                        setShowLottieGesture(false);
                    }, 2000);
                }
            }
        }
    }, [isCarouselVisible]);
    // This condition occurs if due to statement elimination some variable got removed and greater active index is previously stored
    if (!activeVariable) {
        if (questionHasAtleastOneVariable && carrouselActiveVariableIndex !== 0) {
            // dispatch will be triggered with some delay then exception so setting active variable here
            activeVariable = sequencedVariables[0];
            dispatch(
                setCarrouselActiveVariableIndex(
                    instanceId,
                    0,
                    backButtonAlwaysVisible,
                    canLoopVariable || counter.current !== sequencedVariables.length,
                ),
            );
        } else {
            return "";
        }
    }

    const variableHasChoices =
        activeVariable && activeVariable.choiceList && activeVariable.choiceList.length > 0;
    const variableName = activeVariable.name;
    const isSoftPrompt = isQuestionSoftPrompt(question);
    const isRequired = isAnswerRequired(question, questionnaire) && !isSoftPrompt;
    const defaultMinRequiredAnswerValue = 1;

    const getMinRequiredAnswers = React.useCallback(() => {
        const number = getSettingNumber(question, "MinRequiredAnswers");
        if (number !== undefined && !Number.isNaN(number)) {
            return number === 0 ? defaultMinRequiredAnswerValue : number;
        }
        return null;
    }, [question, defaultMinRequiredAnswerValue]);

    const minRequiredAnswers = getMinRequiredAnswers();

    if (
        mentions &&
        Object.keys(mentions).length > 0 &&
        mentions[variableName] &&
        mentions[variableName].length > 0
    ) {
        activeStatementMentionsCount = mentions[variableName].length;
    }
    const activeStatementHasRequiredMentions =
        activeStatementMentionsCount >= (minRequiredAnswers ?? defaultMinRequiredAnswerValue);

    React.useEffect(() => {
        if (isSoftPrompt) {
            if (JSON.stringify(prevErrorMonitorRef.current) === JSON.stringify(errorMonitor)) {
                return;
            }

            prevErrorMonitorRef.current = errorMonitor;

            if (
                !errorMonitor ||
                !question ||
                !sequencedVariables ||
                sequencedVariables.length === 0
            ) {
                return;
            }
            if (errorMonitor.hasOwnProperty(question.name)) {
                const errors = errorMonitor[question.name];
                if (Array.isArray(errors) && errors.length > 0) {
                    const lastError = errors[errors.length - 1];
                    const errorVariableIndex = sequencedVariables.findIndex(
                        (variable) => variable.name === lastError.varInfo,
                    );
                    counter.current = errorVariableIndex + 1;
                    const newPrevBtnActive = backButtonAlwaysVisible || errorVariableIndex > 0;
                    const newNextBtnActive =
                        canLoopVariable || errorVariableIndex < sequencedVariables.length - 1;
                    dispatch(
                        setCarrouselActiveVariableIndex(
                            instanceId,
                            errorVariableIndex,
                            newPrevBtnActive,
                            newNextBtnActive,
                        ),
                    );
                }
            }
        }
    }, [errorMonitor]);

    React.useEffect(() => {
        if (statementValidation.action === "VALIDATEMENTIONS") {
            if (activeStatementHasRequiredMentions) {
                scrollHandler(statementValidation.direction, sequencedVariables);
                setStatementValidationCheck({
                    action: "MENTIONSVALIDATEDSUCCESSFULLY",
                    direction: "NONE",
                });
                dispatch(validationClear(true, true, [questionName]));
            } else {
                setStatementValidationCheck({
                    action: "SHOWMENTIONERROR",
                    direction: "NONE",
                });

                const errorKey =
                    activeStatementMentionsCount === 0 ? "IncompleteAnswer" : "InvalidChoicesMin";

                const minChoices = minRequiredAnswers ?? defaultMinRequiredAnswerValue;

                const questionValidationError = {
                    [questionName]: [
                        { key: errorKey, softPrompt: showSoftPrompt, args: { min: minChoices } },
                    ],
                };

                dispatch(
                    validationSetResult({
                        errorsByVariableName: questionValidationError,
                        isValid: false,
                    }),
                );
            }
        }
    }); // Runs every time cause mention might get changed

    React.useEffect(() => {
        const index = counter.current - 1;
        const canLoop = canLoopVariable || counter.current !== sequencedVariables.length;

        dispatch(
            setCarrouselActiveVariableIndex(instanceId, index, backButtonAlwaysVisible, canLoop),
        );
    }, []); // Runs once on component mount

    const scrollHandler = (direction, vars) => {
        const isPrev = direction === "prev";

        // eslint-disable-next-line no-nested-ternary
        counter.current = isPrev
            ? canLoopVariable && counter.current === 1
                ? vars.length
                : counter.current - 1
            : canLoopVariable && counter.current === vars.length
            ? 1
            : Math.min(counter.current + 1, sequencedVariables.length);

        const newPrevBtnActive = isPrev
            ? backButtonAlwaysVisible || counter.current > 1
            : !hideVariableBackArrow;

        const newNextBtnActive = canLoopVariable || counter.current !== sequencedVariables.length;
        if (directionSaved !== direction) {
            setDirection(direction);
        }
        dispatch(
            setCarrouselActiveVariableIndex(
                instanceId,
                counter.current - 1,
                newPrevBtnActive,
                newNextBtnActive,
            ),
        );
        dispatch(validationClear(true, true, [questionName]));
    };

    const handleNavigationButtonClick = React.useCallback(
        (direction, isAfterSelectionAction = false) => {
            // If no choices is there then no validation check on arrow click (Can be due to elimination process)
            if (variableHasChoices) {
                const notRequiredButMinChoiceIsDefinedValidation =
                    !isRequired &&
                    minRequiredAnswers &&
                    activeStatementMentionsCount !== 0 &&
                    !activeStatementHasRequiredMentions;

                // For automatically advance(radio only) and prev button click move to next statement
                if (isAfterSelectionAction || (direction === "prev" && !canLoopVariable)) {
                    scrollHandler(direction, sequencedVariables);
                } else if (isRequired || notRequiredButMinChoiceIsDefinedValidation) {
                    setStatementValidationCheck({
                        action: "VALIDATEMENTIONS",
                        direction,
                    });
                } else {
                    scrollHandler(direction, sequencedVariables);
                }
            } else {
                scrollHandler(direction, sequencedVariables);
            }
        },
        [
            isRequired,
            sequencedVariables,
            minRequiredAnswers,
            activeStatementMentionsCount,
            activeStatementHasRequiredMentions,
            variableHasChoices,
        ],
    );

    const onAfterCarouselChoiceSelection = React.useCallback(
        (isAfterSelectionAction) => {
            if (
                advanceAutomatically &&
                choiceListAnswerType === QuestionType[QuestionType.RadioButton]
            ) {
                if (sequencedVariables.length - 1 > carrouselActiveVariableIndex) {
                    handleNavigationButtonClick("next", isAfterSelectionAction);
                }
            }
        },
        [
            sequencedVariables,
            carrouselActiveVariableIndex,
            advanceAutomatically,
            choiceListAnswerType,
            handleNavigationButtonClick,
        ],
    );

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const commonProps = {
        isNextBtnActive,
        isPrevBtnActive,
        handleNavigationButtonClick,
        classes,
        carrouselActiveVariableIndex,
        isNumberNavigation,
        sequencedVariables,
        mentions,
    };

    const gap = isMobileView ? MOBILE_VIEW_GAP : DESKTOP_VIEW_GAP;
    const borderWidth = 1;
    const noOfSides = 2; // both side value i.e left and right
    const bordersTotalWidth = noOfSides * borderWidth;
    let statementWidth;
    if (isMobileView && deviceWidth < SMALL_WIDTH) {
        statementWidth =
            deviceWidth -
            (noOfSides * OFFSET_MARGIN_FOR_MOBILE +
                noOfSides * MOBILE_VIEW_GAP +
                noOfSides * PARTIAL_WIDTH_FOR_MOBILE);
    } else if (
        isDesktopPreviewView != null &&
        !isDesktopPreviewView &&
        deviceWidth > MOBILE_WIDTH
    ) {
        statementWidth = MOBILE_DIMENSIONS;
    } else {
        statementWidth = DESKTOP_STATEMENT_WIDTH;
    }
    const itemWidth = statementWidth + bordersTotalWidth;
    const TOTAL_ITEM_WIDTH =
        itemWidth * sequencedVariables.length - 1 + gap * (sequencedVariables.length - 3);
    const transformValue =
        TOTAL_ITEM_WIDTH / 2 -
        itemWidth / 2 +
        gap -
        (itemWidth + gap) * carrouselActiveVariableIndex;
    const handleSwipe = (direction) => {
        handleNavigationButtonClick(direction);
    };
    return (
        <div className={classes.container} ref={containerRef}>
            <div className={classes.statementsContainer}>
                {sequencedVariables.map((variable, index) => (
                    <div
                        key={variable.name}
                        style={{
                            transform: `translateX(${transformValue}px)`,
                            transition: "transform 0.8s ease-in-out",
                        }}
                    >
                        {index === 0 && isShowLottieGesture && isCarouselVisible && (
                            <img src={lottieGif} className="lottie-gesture" />
                        )}
                        <Statement
                            id={variable.elementId}
                            key={variable.name}
                            color={getSettingString(variable, "Color", "#F2F2F2")}
                            text={translate(variable, "text", language)}
                            imageUrl={translate(variable, "imageUrl", language)}
                            isMobileView={isMobileView}
                            isActiveItem={carrouselActiveVariableIndex === index}
                            statementWidth={statementWidth}
                            handleSwipe={handleSwipe}
                            isShowLottieGesture={isShowLottieGesture}
                        />
                    </div>
                ))}
            </div>
            {isAboveChoiceNavigation ? (
                <WrapperWithNavigationButtons
                    {...commonProps}
                    backButtonText={backButtonAriaLabelText}
                    nextButtonText={nextButtonAriaLabelText}
                />
            ) : (
                ""
            )}
            <div className={classes.choiceAndNavigationContainer}>
                <ControlContainer
                    controlType={ChoiceListControl}
                    onAfterSelection={() => {
                        onAfterCarouselChoiceSelection(true);
                    }}
                    isSingleOnPage={props.isSingleOnPage}
                    onNavigation={props.onNavigation}
                    {...getRendererProps(props)}
                />
                {isBelowChoiceNavigation ? (
                    <WrapperWithNavigationButtons
                        {...commonProps}
                        backButtonText={backButtonAriaLabelText}
                        nextButtonText={nextButtonAriaLabelText}
                    />
                ) : (
                    ""
                )}
            </div>
        </div>
    );
}
